import type {ComponentStageData, ComponentStageHeuristicDefinition, ConversionDal} from '../../types'
import {LEFT, RIGHT, CENTER, textAlignmentToPosition} from './constants'
import type {Pointer} from '@wix/document-services-types'
import {updateStyle, updateTargetLayout} from '../common'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {getStructurePointer, getTargetLayoutPointer, getStylePointer} from '../../conversionDal/utils/pointer'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {ComponentAlignment} from './types'
import _ from 'lodash'
import type {StyleEntryItem} from '../../conversionDal/types'

const updateComponentAlignment = (componentStageData: ComponentStageData, alignment: ComponentAlignment): void => {
    if (!textAlignmentToPosition[alignment]) {
        return
    }

    const {componentPointer, conversionDal} = componentStageData
    const {semanticType} = conversionDal.get(componentPointer)
    const {itemLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer)))
    itemLayout.justifySelf = textAlignmentToPosition[alignment]
    updateTargetLayout(componentStageData, {itemLayout})

    if (semanticType === SemanticTypes.TEXT) {
        const {alignment: TEXT_ALIGNMENT}: StyleEntryItem = conversionDal.get(getStylePointer(componentPointer)) ?? {}
        if (!TEXT_ALIGNMENT) return
        const newAlignment = _.cloneDeep(TEXT_ALIGNMENT)
        newAlignment.forEach(textAlignment => {
            textAlignment.data = alignment
        })
        updateStyle(componentStageData, {alignment: newAlignment})
    }
}

const checkParentAlignment = (
    componentPointer: Pointer,
    conversionDal: ConversionDal,
    alignmentDirection: ComponentAlignment
): boolean => {
    const {parent} = conversionDal.get(componentPointer)
    const {alignment} = conversionDal.get(getStructurePointer({id: parent})) ?? {alignment: CENTER}
    return alignment === alignmentDirection
}

export const alignmentHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, config}: ComponentStageData) => {
            return componentPointer.id === config.rootPointer.id
        },
        apply: () => {}
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, LEFT)
        },
        apply: (componentStageData: ComponentStageData) => updateComponentAlignment(componentStageData, LEFT),
        heuristicCode: 'COMPONENTS ALIGNED LEFT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, RIGHT)
        },
        apply: (componentStageData: ComponentStageData) => updateComponentAlignment(componentStageData, RIGHT),
        heuristicCode: 'COMPONENTS ALIGNED RIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            return checkParentAlignment(componentPointer, conversionDal, CENTER)
        },
        apply: (componentStageData: ComponentStageData) => updateComponentAlignment(componentStageData, CENTER),
        heuristicCode: 'COMPONENTS MIXED ALIGNMENT'
    }
]
